import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';

import { useRegisterMutation } from '@/hooks/queries/useAuth';
import { UserBasicInfo } from '@/components/users/user-basic-info';
import { UserRoleAccess } from '@/components/users/user-role-access';
import { createUserSchema, type CreateUserFormData } from '@/schema/user';
import { cn } from '@/lib/utils';

interface CreateUserFormProps {
  className?: string;
}

export function CreateUserForm({ className }: CreateUserFormProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Initialize React Hook Form with Zod validation
  const form = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'auditor', // Default role instead of undefined
      property_id: undefined,
    },
  });

  // Initialize the registration mutation
  const registerMutation = useRegisterMutation({
    onSuccessCallback: () => {
      // Reset form after successful creation
      form.reset();

      // Navigate back to users list
      navigate('/users');
    },
  });

  // Use mutation's pending state for loading
  const isSubmitting = registerMutation.isPending;

  // Form submission handler
  const onSubmit = (data: CreateUserFormData) => {
    // Prepare API request data
    const registerData = {
      name: data.name,
      email: data.email,
      password: data.password,
      role: data.role,
      property_id: data.property_id,
      // Optional fields can be added here
      phone: undefined,
      department: undefined,
    };

    // Call the registration API
    registerMutation.mutate(registerData);
  };

  const handleCancel = () => {
    navigate('/users');
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className={className}>
        {/* Form Content */}
        <div className="space-y-6">
          <UserBasicInfo form={form} />
          <UserRoleAccess form={form} />
        </div>

        {/* Navigation Buttons */}
        <div
          className={cn(
            'flex gap-4 pt-8 border-t mt-8 justify-end',
            isRTL ? 'flex-row-reverse' : 'flex-row'
          )}
        >
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            {t('cancel')}
          </Button>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-accent hover:bg-accent/90 text-accent-foreground disabled:opacity-50 w-full sm:w-auto"
          >
            {isSubmitting
              ? t('users.create.buttons.creating')
              : t('users.create.buttons.createUser')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
